/* eslint-disable @typescript-eslint/no-explicit-any */
import type { FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query";
import type { EnvironmentConfig } from "@/config/environment";
import { getSecurityHeaders } from "@/components/security/security-headers";
import { generateRequestId } from "./app-utils";
import type { ApiErrorDetail } from "@/types/core";

/** Allocation-light request metadata for local diagnostics — never includes secrets or bodies. */
function logRequestMetadata(
  operation: string,
  method: string,
  url: string,
  headers: Headers,
  config: EnvironmentConfig
): void {
  if (!(import.meta.env.DEV && config.debug.enabled)) {
    return;
  }

  console.log(`[api] ${operation}`, {
    method,
    url,
    correlationId: headers.get("X-Correlation-ID") ?? undefined,
    authenticated: headers.has("authorization")
  });
}

// API Utility Functions

export async function buildAppHTTPHeaders(
  headers: Headers,
  config: EnvironmentConfig
): Promise<Headers> {
  // Add common headers
  headers.set("Content-Type", "application/json");
  headers.set("Accept", "application/json");
  headers.set("User-Agent", `${config.app.name}/${config.app.version}`);

  // Never attach browser document security headers to outbound API requests.
  const securityHeaders = getSecurityHeaders();
  Object.entries(securityHeaders).forEach(([key, value]) => {
    if (
      key === "Content-Security-Policy" ||
      key === "X-Frame-Options" ||
      key === "X-Content-Type-Options" ||
      key === "Referrer-Policy" ||
      key === "Permissions-Policy"
    ) {
      return;
    }
    headers.set(key, value);
  });

  // Add API-specific security headers
  headers.set("X-API-Version", config.api.version.toString());
  headers.set("X-Client-ID", config.msal.clientId);

  // Add correlation ID for tracing
  const correlationId = generateRequestId();
  headers.set("X-Correlation-ID", correlationId);

  // Add timestamp for request tracking
  headers.set("X-Request-Timestamp", new Date().toISOString());

  return headers;
}

// Helper function to convert Headers to plain object safely
export function headersToObject(headers: Headers): Record<string, string> {
  return Object.fromEntries(headers.entries());
}

/**
 * Drop unset query values so they are not serialized as empty strings
 * (e.g. `startDate=`), which fails DateOnly model binding on the API.
 */
export function omitEmptyQueryParams<T extends Record<string, unknown>>(
  params?: T
): Record<string, unknown> | undefined {
  if (!params) {
    return undefined;
  }

  const next: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) {
      continue;
    }
    if (typeof value === "string" && value.trim() === "") {
      continue;
    }
    if (Array.isArray(value) && value.length === 0) {
      continue;
    }
    next[key] = value;
  }

  return Object.keys(next).length > 0 ? next : undefined;
}

export async function buildHttpGet<T extends Record<string, unknown>>(
  config: EnvironmentConfig,
  addAuthHeader: (headers: Headers) => Promise<Headers>,
  url: string,
  params?: T
): Promise<FetchArgs> {
  let headers = new Headers();
  headers = await buildAppHTTPHeaders(headers, config);
  headers = await addAuthHeader(headers);
  const headerObject = headersToObject(headers);

  logRequestMetadata("buildHttpGet", "GET", url, headers, config);

  return {
    url: url,
    method: "GET",
    params: omitEmptyQueryParams(params),
    headers: headerObject
  };
}

export async function buildHttpGetForDownload<
  T extends Record<string, unknown>
>(
  config: EnvironmentConfig,
  addAuthHeader: (headers: Headers) => Promise<Headers>,
  url: string,
  params?: T
): Promise<FetchArgs> {
  const fetchArgs = await buildHttpGet(config, addAuthHeader, url, params);

  return {
    ...fetchArgs,
    responseHandler: async (response) => {
      const contentType = response.headers.get("content-type");

      // checking if content-type includes json and not application/json because it can be appilcation/problem+json
      if (contentType?.includes("json")) {
        return response.json();
      }

      const blob = await response.blob();

      // Extract filename from content-disposition header
      const contentDisposition = response.headers.get("content-disposition");
      let filename = "file";

      // The current implementation is not exposing content-disposition header on the response so it will be null
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(
          /filename="?([^";\n]+)"?/
        );
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Trigger download immediately
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      return { success: true, filename };
    }
  };
}

export async function buildHttpPost<T>(
  config: EnvironmentConfig,
  addAuthHeader: (headers: Headers) => Promise<Headers>,
  url: string,
  body: T
): Promise<FetchArgs> {
  let headers = new Headers();
  headers = await buildAppHTTPHeaders(headers, config);
  headers = await addAuthHeader(headers);
  // If sending FormData, let the browser set the multipart boundary
  if (body instanceof FormData) {
    headers.delete("Content-Type");
  }
  const headerObject = headersToObject(headers);

  logRequestMetadata("buildHttpPost", "POST", url, headers, config);

  return {
    url: url,
    method: "POST",
    body: body,
    headers: headerObject
  };
}

export async function buildHttpPut<T>(
  config: EnvironmentConfig,
  addAuthHeader: (headers: Headers) => Promise<Headers>,
  url: string,
  body: T
): Promise<FetchArgs> {
  let headers = new Headers();
  headers = await buildAppHTTPHeaders(headers, config);
  headers = await addAuthHeader(headers);
  const headerObject = headersToObject(headers);

  logRequestMetadata("buildHttpPut", "PUT", url, headers, config);

  return {
    url: url,
    method: "PUT",
    body: body,
    headers: headerObject
  };
}

export async function buildHttpPatch<T>(
  config: EnvironmentConfig,
  addAuthHeader: (headers: Headers) => Promise<Headers>,
  url: string,
  body: T
): Promise<FetchArgs> {
  let headers = new Headers();
  headers = await buildAppHTTPHeaders(headers, config);
  headers = await addAuthHeader(headers);
  const headerObject = headersToObject(headers);

  logRequestMetadata("buildHttpPatch", "PATCH", url, headers, config);

  return {
    url: url,
    method: "PATCH",
    body: body,
    headers: headerObject
  };
}

export async function buildHttpDelete(
  config: EnvironmentConfig,
  addAuthHeader: (headers: Headers) => Promise<Headers>,
  url: string
): Promise<FetchArgs> {
  let headers = new Headers();
  headers = await buildAppHTTPHeaders(headers, config);
  headers = await addAuthHeader(headers);
  const headerObject = headersToObject(headers);

  logRequestMetadata("buildHttpDelete", "DELETE", url, headers, config);

  return {
    url: url,
    method: "DELETE",
    headers: headerObject
  };
}

/**
 * Central unwrap for the unified API response envelope (`{ status, data, error, meta }`).
 *
 * On success it returns the inner `data`, so feature code keeps consuming the raw
 * payload type. On error it surfaces the structured `error` object as the RTK
 * `FetchBaseQueryError.data`. Non-enveloped responses (file downloads, health
 * probes, bodiless 204s) pass through unchanged.
 *
 * Applied once in each base query so no individual endpoint needs a `transformResponse`.
 */
export function unwrapApiEnvelope<T extends { data?: any; error?: any }>(
  result: T
): T {
  // Error path: surface the structured `error` object.
  if (result.error) {
    const body = result.error.data;
    if (isApiEnvelope(body) && body.success === false) {
      const apiError = body.error;
      return {
        ...result,
        error: {
          ...result.error,
          data: {
            code: apiError?.code,
            message: apiError?.message ?? body.message,
            type: apiError?.type,
            details: apiError?.details,
            status: body.status,
            correlationId: body.meta.correlationId,
            timestamp: body.meta.timestamp,
            version: body.meta.version
          } satisfies ApiErrorDetail
        }
      };
    }
    return result;
  }

  // Success path: unwrap `data`.
  if (isApiEnvelope(result.data)) {
    return { ...result, data: result.data.data };
  }

  return result;
}

/**
 * Type guard for the unified envelope. Discriminated by a numeric `status`, a boolean
 * `success`, and a `meta` object — enough to avoid false positives against domain DTOs
 * and non-enveloped responses (health probes, file downloads).
 */
function isApiEnvelope(
  body: unknown
): body is {
  success: boolean;
  status: number;
  message: string;
  data?: unknown;
  error?: {
    code?: string;
    message?: string;
    type?: string;
    details?: ApiErrorDetail["details"];
  };
  meta: { correlationId?: string; timestamp?: string; version?: string };
} {
  if (!body || typeof body !== "object") return false;

  const candidate = body as Record<string, unknown>;
  const status = candidate.status;
  const success = candidate.success;
  const meta = candidate.meta;

  return (
    typeof status === "number" &&
    typeof success === "boolean" &&
    success === (status >= 200 && status < 300) &&
    typeof candidate.message === "string" &&
    !!meta &&
    typeof meta === "object"
  );
}

/** Extracts normalized API error details from an RTK Query error or returns null. */
export function getApiErrorDetail(error: unknown): ApiErrorDetail | null {
  if (!error || typeof error !== "object") return null;

  if ("data" in error) {
    const data = (error as { data?: unknown }).data;
    return data && typeof data === "object" ? (data as ApiErrorDetail) : null;
  }

  return "message" in error || "code" in error
    ? (error as ApiErrorDetail)
    : null;
}

function uniqueNonEmptyMessages(messages: Array<string | undefined>): string[] {
  const seen = new Set<string>();
  const result: string[] = [];

  for (const message of messages) {
    const trimmed = message?.trim();
    if (!trimmed || seen.has(trimmed)) continue;
    seen.add(trimmed);
    result.push(trimmed);
  }

  return result;
}

function collectApiErrorMessages(apiError: ApiErrorDetail | null): string[] {
  if (!apiError) return [];

  const nestedError =
    "error" in apiError &&
    apiError.error &&
    typeof apiError.error === "object"
      ? (apiError as ApiErrorDetail & { error?: ApiErrorDetail }).error
      : undefined;

  const details = apiError.details ?? nestedError?.details;
  const detailMessages = uniqueNonEmptyMessages(
    details?.map((detail) => detail.message) ?? []
  );
  if (detailMessages.length > 0) {
    return detailMessages;
  }

  const message = apiError.message ?? nestedError?.message;
  return message?.trim() ? [message.trim()] : [];
}

/**
 * Field-level validation messages from a unified-envelope API error.
 * Prefers `error.details[].message` so FluentValidation failures are visible.
 */
export function getErrorMessages(error: unknown): string[] {
  if (!error) return ["Unknown error"];

  if ("status" in (error as object)) {
    const err = error as FetchBaseQueryError;

    if (typeof err.status === "string") {
      return ["Something went wrong"];
    }

    const messages = collectApiErrorMessages(getApiErrorDetail(err));
    if (messages.length > 0) {
      return messages;
    }

    return [`Error ${err.status}`];
  }

  if ("message" in (error as object)) {
    return ["Something went wrong"];
  }

  return ["An unexpected error occurred"];
}

export function getErrorMessage(error: unknown): string {
  return getErrorMessages(error).join(" ");
}
